import { BookingQuestionId } from "@b2bportal/activities-api";
import type {
  ActivityReservation,
  BookingQuestionAnswer,
} from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import { calculateEndTimeLabel } from "../utils/calculateEndTimeLabel";

export interface ActivityBookingQuestionsSectionState {
  answers: BookingQuestionAnswer[];
  inclusions: string[];
  exclusions: string[];
  languageLabel: string | null;
  specialRequirements: string | null;
  hasBookingQuestions: boolean;
  hasInclusions: boolean;
  hasContent: boolean;
  startTimeLabel: string | null;
  endTimeLabel: string | null;
  pickupInfo: {
    label: string;
    location?: string;
  } | null;
  meetupLocation: string | null;
  dropOffLocation: string | null;
  hasStartTimeOrPickup: boolean;
}

export function useActivityBookingQuestionsSection(
  reservation: ActivityReservation
): ActivityBookingQuestionsSectionState {
  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const answers = reservation.bookingDetails?.bookingQuestionAnswers ?? [];
  const inclusionsAndExclusions =
    reservation.activityDetails?.inclusionsAndExclusions;
  const inclusions = inclusionsAndExclusions?.inclusions ?? [];
  const exclusions = inclusionsAndExclusions?.exclusions ?? [];
  const languageLabel =
    reservation.activitySelection?.languageGuide?.languageDisplayName ?? null;
  const specialRequirementsAnswer = answers.find(
    (question: BookingQuestionAnswer) =>
      question.bookingQuestionId === BookingQuestionId.SpecialRequirements
  );
  const specialRequirements = specialRequirementsAnswer?.answer?.trim() || null;

  // Get start time information
  const selection = reservation.activitySelection;
  const startDate = selection?.travelDate ? dayjs(selection.travelDate) : null;
  const startTime = selection?.providerActivityStartTime;
  const startTimeLabel =
    startDate && startTime
      ? t("activities.postBooking.localTimeLabel", {
          date: formatDateTime(
            startDate.toDate(),
            DateTimeFormatStyle.ShortMonthDayShortWeekday
          ),
          time: startTime,
        })
      : null;

  const durationMinutes = reservation.activityDetails?.duration ?? 0;
  const endTimeLabel = startDate
    ? calculateEndTimeLabel({
        startDate,
        startTime,
        durationMinutes,
        t,
        formatDateTime,
        DateTimeFormatStyle,
      })
    : null;

  // Get pickup information from booking question answers
  const pickupAnswer = answers.find(
    (question: BookingQuestionAnswer) =>
      question.bookingQuestionId === BookingQuestionId.PickupPoint
  );
  const pickupAnswerValue = pickupAnswer?.answer?.trim() ?? null;

  let pickupInfo: { label: string; location?: string } | null = null;
  if (pickupAnswerValue) {
    if (pickupAnswerValue === "CONTACT_SUPPLIER_LATER") {
      pickupInfo = {
        label: t("activities.postBooking.details.pickupArrangeLater"),
      };
    } else if (pickupAnswerValue === "MEET_AT_DEPARTURE_POINT") {
      pickupInfo = {
        label: t("activities.postBooking.details.pickupNotNeeded"),
      };
    } else {
      pickupInfo = {
        label: t("activities.postBooking.details.pickupArrangeNow"),
        location: pickupAnswerValue,
      };
    }
  }

  const meetupLocation =
    reservation.activityDetails?.logistics?.startLocations[0]?.name ?? null;

  const dropOffLocation =
    reservation.activityDetails?.logistics?.endLocations[0]?.name ?? null;

  const hasBookingQuestions = answers.length > 0;
  const hasInclusions = inclusions.length > 0 || exclusions.length > 0;
  const hasStartTimeOrPickup = Boolean(
    startTimeLabel ||
      endTimeLabel ||
      pickupInfo ||
      meetupLocation ||
      dropOffLocation
  );
  const hasContent = Boolean(
    hasBookingQuestions ||
      hasInclusions ||
      languageLabel ||
      specialRequirements ||
      hasStartTimeOrPickup
  );

  return {
    answers,
    inclusions,
    exclusions,
    languageLabel,
    specialRequirements,
    hasBookingQuestions,
    hasInclusions,
    hasContent,
    startTimeLabel,
    endTimeLabel,
    pickupInfo,
    meetupLocation,
    dropOffLocation,
    hasStartTimeOrPickup,
  };
}
